import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import {
  ArrowRight, ArrowLeft, Check, Sparkles, Landmark, ShieldCheck,
  Camera, Trash2, Lock, MapPin, Wand2,
} from 'lucide-react';
import api from '@/services/api';
import { errorDetail } from '@/utils/apiError';
import { buildBioTemplate } from '@/utils/bioTemplate';
import { toast } from 'sonner';
import CredentialsSection from '@/components/CredentialsSection';
import PhoneInput from '@/components/PhoneInput';
import OndaProgressO from '@/components/OndaProgressO';
import GoldenScissors from '@/components/GoldenScissors';
import SocialLinkPreview from '@/components/SocialLinkPreview';
import { normalizeWebsite } from '@/utils/socials';

const SERVICES = [
  { key: 'haircut', label: 'Haircut' },
  { key: 'coloring', label: 'Colouring' },
  { key: 'styling', label: 'Styling' },
  { key: 'facial', label: 'Facial' },
  { key: 'nails', label: 'Nails' },
  { key: 'threading', label: 'Threading' },
  { key: 'waxing', label: 'Waxing' },
  { key: 'cosmetic_tattoo', label: 'Cosmetic Tattoo' },
  { key: 'massage', label: 'Massage' },
];

const SOCIAL_FIELDS = [
  { key: 'instagram', label: 'Instagram', placeholder: '@yourusername' },
  { key: 'tiktok', label: 'TikTok', placeholder: '@yourusername' },
  { key: 'facebook', label: 'Facebook', placeholder: 'facebook.com/yourpage' },
  { key: 'youtube', label: 'YouTube', placeholder: 'youtube.com/@yourchannel' },
  { key: 'twitter', label: 'Twitter / X', placeholder: '@yourusername' },
  { key: 'website', label: 'Website', placeholder: 'https://yourwebsite.com' },
];

// Evidence required before Stripe Identity validation (mirrors the backend gate).
const MIN_DOCUMENTS = 3;
const MIN_PHOTOS = 3;

// Named steps — never hardcode indices (the ?identity=done deep link and the
// footer action switch both depend on these).
const STEP_WELCOME = 0;
const STEP_PROFILE = 1;
const STEP_PRICING = 2;
const STEP_AREA = 3;
const STEP_DOCS = 4;
const STEP_PHOTOS = 5;
const STEP_VERIFY = 6;
const STEP_CONFIRM = 7;
const STEP_PAYOUT = 8;
const STEP_DONE = 9;

const STEPS = ['Welcome', 'Profile', 'Services & Pricing', 'Service Area', 'Documents', 'Photos', 'Verify ID', 'Confirm', 'Get Paid', 'All Set'];

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEP_WELCOME);
  const [saving, setSaving] = useState(false);

  // Personal details (editable — stylists can fix a mistyped name/number right here)
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [currentPhone, setCurrentPhone] = useState('');
  const [newPhone, setNewPhone] = useState('');

  const [bio, setBio] = useState('');
  const [years, setYears] = useState('');
  const [skills, setSkills] = useState([]);
  const [socials, setSocials] = useState({ instagram: '', tiktok: '', facebook: '', youtube: '', twitter: '', website: '' });
  const [pricing, setPricing] = useState({}); // key -> { price_min, price_max, duration_minutes }
  const [address, setAddress] = useState('');
  const [radius, setRadius] = useState(10);
  const [identityStatus, setIdentityStatus] = useState(null);

  // Address map-confirm: preview holds the server-geocoded pin for the exact
  // string that was checked. Only ever fetched on an explicit button click.
  const [preview, setPreview] = useState(null); // {lat, lng, formatted_address, checked_input}
  const [checking, setChecking] = useState(false);
  const [addressError, setAddressError] = useState('');

  // Verification evidence progress
  const [docsCount, setDocsCount] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [hasAbn, setHasAbn] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  const socialCount = Object.values(socials).filter((v) => v && v.trim()).length;
  const requirementsMet = docsCount >= MIN_DOCUMENTS && photos.length >= MIN_PHOTOS && hasAbn && socialCount >= 1;
  const isVerified = identityStatus === 'verified';

  const fetchPhotos = useCallback(async () => {
    try {
      const { data } = await api.get('/credentials/portfolio');
      setPhotos(data);
    } catch (e) { /* not fatal */ }
  }, []);

  const refreshProgress = useCallback(async () => {
    try {
      const { data } = await api.get('/stylists/verification-progress');
      setIdentityStatus(data.identity_status);
      setDocsCount(data.documents_uploaded);
      setHasAbn(!!data.business_license_uploaded);
    } catch (e) { /* not fatal */ }
  }, []);

  useEffect(() => {
    (async () => {
      // Hydrate concurrently, then handle the Stripe return — jumping to the Verify
      // step before counts load would flash "you haven't uploaded anything".
      await Promise.allSettled([
        refreshProgress(),
        api.get('/auth/me').then(({ data: me }) => {
          setFirstName(me.first_name || '');
          setLastName(me.last_name || '');
          setCurrentPhone(me.phone || '');
        }),
        api.get('/stylists/profile/me').then(({ data }) => {
          // Re-entering the wizard keeps what's already saved (bio reloaded blank before).
          if (data.bio) setBio(data.bio);
          if (data.years_experience != null) setYears(String(data.years_experience));
          if (data.skills?.length) setSkills(data.skills);
          if (data.social_links) setSocials((prev) => ({ ...prev, ...data.social_links }));
        }),
        fetchPhotos(),
      ]);
      const params = new URLSearchParams(window.location.search);
      if (params.get('identity') === 'done') {
        toast.success('Verification submitted');
        window.history.replaceState({}, '', '/onboarding');
        setStep(STEP_VERIFY);
        return;
      }
      // Smart resume — returning stylists land on their first INCOMPLETE step,
      // not back at Welcome.
      try {
        const [{ data: status }, { data: prog }] = await Promise.all([
          api.get('/stylists/onboarding-status'),
          api.get('/stylists/verification-progress'),
        ]);
        const missing = status.missing || [];
        if (missing.includes('services')) setStep(STEP_PROFILE);
        else if (missing.includes('pricing')) setStep(STEP_PRICING);
        else if (missing.includes('service_area')) setStep(STEP_AREA);
        else if (prog.documents_uploaded < prog.documents_required || !prog.business_license_uploaded) setStep(STEP_DOCS);
        else if (prog.photos_uploaded < prog.photos_required) setStep(STEP_PHOTOS);
        else if (prog.identity_status !== 'verified') setStep(STEP_VERIFY);
        else setStep(STEP_CONFIRM);
      } catch (e) { /* stay on Welcome */ }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- one-shot mount hydration; callbacks are stable useCallbacks

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const toggleSkill = (key) =>
    setSkills((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));

  const setPrice = (key, field, value) =>
    setPricing((p) => ({ ...p, [key]: { ...p[key], [field]: value } }));

  const setSocial = (key, value) => setSocials((prev) => ({ ...prev, [key]: value }));

  // Merged + trimmed socials for saving; bare website domains get https:// prepended
  // (backend normalizes too, but don't make the round trip to find out).
  const socialsPayload = () => {
    const out = {};
    for (const [k, v] of Object.entries(socials)) {
      out[k] = k === 'website' ? normalizeWebsite(v) : (v || '').trim();
    }
    return out;
  };

  const writeBioForMe = () => {
    const draft = buildBioTemplate({ firstName, years, skills });
    if (bio.trim() && !window.confirm('Replace your current bio with a fresh draft?')) return;
    setBio(draft);
    toast.success('Bio drafted — make it yours! Replace the [brackets] with your details.');
  };

  const saveProfile = async () => {
    if (!firstName.trim()) { toast.error('Please enter your first name'); return; }
    if (skills.length === 0) { toast.error('Pick at least one service you offer'); return; }
    setSaving(true);
    try {
      const details = { first_name: firstName.trim(), last_name: lastName.trim() };
      if (newPhone) details.phone = newPhone;
      const { data: me } = await api.patch('/auth/me', details);
      setCurrentPhone(me.phone || currentPhone);
      setNewPhone('');
      // social_links is replaced whole server-side — always send the full merged dict.
      await api.put('/stylists/profile/update', {
        bio,
        years_experience: years ? parseInt(years, 10) : null,
        skills,
        social_links: socialsPayload(),
      });
      next();
    } catch (e) {
      toast.error(errorDetail(e, 'Could not save your profile'));
    } finally {
      setSaving(false);
    }
  };

  const savePricing = async () => {
    const items = skills.map((k) => ({
      service: k,
      price_min: parseFloat(pricing[k]?.price_min ?? 50),
      price_max: parseFloat(pricing[k]?.price_max ?? 150),
      duration_minutes: parseInt(pricing[k]?.duration_minutes ?? 60, 10),
    }));
    if (items.some((i) => !(i.price_min > 0) || i.price_max < i.price_min)) {
      toast.error('Check your prices — max should be at least the min');
      return;
    }
    setSaving(true);
    try {
      await api.put('/stylists/pricing', items);
      next();
    } catch (e) {
      toast.error('Could not save pricing');
    } finally {
      setSaving(false);
    }
  };

  const checkAddress = async () => {
    if (!address.trim()) { setAddressError('Enter your full service address first.'); return; }
    setChecking(true);
    setAddressError('');
    try {
      const { data } = await api.post('/stylists/service-area/preview', { address: address.trim() });
      setPreview({ ...data, checked_input: address.trim() });
    } catch (e) {
      setPreview(null);
      setAddressError(e.response?.data?.detail || 'Could not verify that address. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const previewMatches = preview && preview.checked_input === address.trim();

  const saveArea = async () => {
    if (!previewMatches) { toast.error('Check your address on the map first'); return; }
    setSaving(true);
    try {
      // Backend geocodes the address (source of truth) and rejects fake/vague ones.
      await api.put('/stylists/service-area', {
        address: address.trim(),
        service_radius_miles: Number(radius),
      });
      next();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save your area');
    } finally {
      setSaving(false);
    }
  };

  const uploadPhotos = async (files) => {
    if (!files?.length) return;
    setUploadingPhotos(true);
    let uploaded = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('photo', file);
        fd.append('photo_type', 'work_sample');
        await api.post('/credentials/portfolio/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploaded += 1;
      } catch (e) {
        toast.error(e.response?.data?.detail || `Could not upload ${file.name}`);
      }
    }
    if (uploaded > 0) toast.success(`${uploaded} photo${uploaded > 1 ? 's' : ''} uploaded`);
    try {
      await fetchPhotos();
    } finally {
      setUploadingPhotos(false);
    }
  };

  const deletePhoto = async (photoId) => {
    // Optimistic removal — also guards against a double-click firing a second
    // DELETE that 404s and shows a misleading error toast.
    setPhotos((prev) => prev.filter((p) => p.photo_id !== photoId));
    try {
      await api.delete(`/credentials/portfolio/${photoId}`);
    } catch (e) {
      toast.error('Could not delete the photo');
    } finally {
      await fetchPhotos();
    }
  };

  const saveProfileAndContinue = async () => {
    // The Onda-O confirmation: persist the final profile, then straight to payouts.
    setSaving(true);
    try {
      await api.put('/stylists/profile/update', {
        bio,
        years_experience: years ? parseInt(years, 10) : null,
        skills: skills.length ? skills : null,
        social_links: socialsPayload(),
      });
      toast.success('Profile saved');
      next();
    } catch (e) {
      toast.error(errorDetail(e, 'Could not save your profile'));
    } finally {
      setSaving(false);
    }
  };

  const startPayouts = async () => {
    setSaving(true);
    try {
      const { data } = await api.post('/payouts/connect/start');
      window.location.href = data.url; // Stripe-hosted Express onboarding (bank + ID)
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start payout setup');
      setSaving(false);
    }
  };

  const startIdentity = async () => {
    setSaving(true);
    try {
      const { data } = await api.post('/stylists/identity/verify');
      window.location.href = data.url; // Stripe Identity hosted flow (ID + selfie + liveness)
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start verification');
      setSaving(false);
    }
  };

  const stepBody = () => {
    switch (step) {
      case STEP_WELCOME:
        return (
          <div className="text-center space-y-5">
            <Sparkles className="h-12 w-12 text-primary mx-auto" />
            <h2 className="font-cormorant text-4xl font-bold">Welcome to Onda</h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              Let's get your stylist profile ready — your details, services, pricing, the area you cover,
              your credentials and photos, then a quick identity check. Payouts unlock once you're verified.
            </p>
            <Button className="btn-primary px-10 py-6 text-lg" onClick={next}>
              Let's go <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        );
      case STEP_PROFILE:
        return (
          <div className="space-y-6">
            <h2 className="font-cormorant text-3xl font-bold">About you</h2>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="first">First name</Label>
                <Input id="first" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last">Last name</Label>
                <Input id="last" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Mobile number</Label>
              {currentPhone && !newPhone && (
                <p className="text-sm text-muted-foreground">
                  Current: <span className="text-foreground font-medium">{currentPhone}</span> — enter below to change it.
                </p>
              )}
              <PhoneInput onChange={setNewPhone} />
            </div>
            <div className="space-y-2">
              <Label>Services you offer</Label>
              <div className="flex flex-wrap gap-2">
                {SERVICES.map((s) => (
                  <button key={s.key} type="button" onClick={() => toggleSkill(s.key)}
                    className={`px-4 py-2 rounded-full border text-sm transition-colors ${
                      skills.includes(s.key)
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'border-border text-foreground/80 hover:border-primary/50'
                    }`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="years">Years of experience</Label>
              <Input id="years" type="number" min="0" value={years}
                onChange={(e) => setYears(e.target.value)} placeholder="e.g. 5" className="max-w-[160px]" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="bio">Short bio</Label>
                <Button type="button" variant="outline" size="sm" onClick={writeBioForMe}>
                  <Wand2 className="mr-2 h-4 w-4" /> Write it for me
                </Button>
              </div>
              <Textarea id="bio" value={bio} onChange={(e) => setBio(e.target.value)}
                placeholder="Tell clients about yourself, your training, and what makes you unique…"
                className="h-[240px] resize-none overflow-y-auto" maxLength={1000} />
              <p className="text-xs text-muted-foreground">{bio.length}/1000 — tip: tick your services first, then let us draft it.</p>
            </div>
            <div className="space-y-3 rounded-lg border border-border/50 p-4">
              <div className="flex items-center justify-between">
                <Label>Your socials — be seen AND heard</Label>
                <OndaProgressO met={socialCount >= 1} label="At least 1 connected" />
              </div>
              <p className="text-xs text-muted-foreground">
                Clients check you out before they book. Link at least one — your work deserves the audience.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {SOCIAL_FIELDS.map((f) => (
                  <div key={f.key} className="space-y-1">
                    <Label htmlFor={`social-${f.key}`} className="text-xs">{f.label}</Label>
                    <Input id={`social-${f.key}`} value={socials[f.key] || ''} placeholder={f.placeholder}
                      onChange={(e) => setSocial(f.key, e.target.value)} />
                    <SocialLinkPreview network={f.key} value={socials[f.key]} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case STEP_PRICING:
        return (
          <div className="space-y-6">
            <h2 className="font-cormorant text-3xl font-bold">Your services & pricing</h2>
            <p className="text-sm text-muted-foreground">Set a price range and typical duration for each service.</p>
            <div className="space-y-4">
              {skills.map((k) => {
                const svc = SERVICES.find((s) => s.key === k);
                return (
                  <div key={k} className="rounded-lg border border-border/50 p-4">
                    <p className="font-semibold mb-3">{svc?.label}</p>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">Min $</Label>
                        <Input type="number" min="0" placeholder="50"
                          value={pricing[k]?.price_min ?? ''} onChange={(e) => setPrice(k, 'price_min', e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-xs">Max $</Label>
                        <Input type="number" min="0" placeholder="150"
                          value={pricing[k]?.price_max ?? ''} onChange={(e) => setPrice(k, 'price_max', e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-xs">Mins</Label>
                        <Input type="number" min="0" placeholder="60"
                          value={pricing[k]?.duration_minutes ?? ''} onChange={(e) => setPrice(k, 'duration_minutes', e.target.value)} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      case STEP_AREA:
        return (
          <div className="space-y-6">
            <h2 className="font-cormorant text-3xl font-bold">Where you're based</h2>
            <p className="text-sm text-muted-foreground">
              Enter your real service address, then check it on the map so you know we've pinned the right
              place. Your exact address is never shown publicly; clients only ever see your suburb.
            </p>
            <div className="space-y-2">
              <Label htmlFor="area">Service address</Label>
              <div className="flex gap-2">
                <Input id="area" value={address}
                  onChange={(e) => { setAddress(e.target.value); setAddressError(''); }}
                  placeholder="e.g. 12 Cavill Ave, Surfers Paradise QLD" className="flex-1" />
                <Button type="button" variant="outline" onClick={checkAddress} disabled={checking}>
                  {checking ? <GoldenScissors size="sm" /> : <MapPin className="mr-2 h-4 w-4" />}
                  {checking ? '' : 'Check address'}
                </Button>
              </div>
              {addressError && <p className="text-sm text-red-400">{addressError}</p>}
            </div>
            {previewMatches && (
              <div className="space-y-2 rounded-lg border border-primary/30 overflow-hidden">
                <iframe
                  title="Your service address"
                  src={`https://maps.google.com/maps?q=${preview.latitude},${preview.longitude}&z=15&output=embed`}
                  className="w-full h-[240px] border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <p className="text-sm px-3 pb-3">
                  <span className="text-green-500 font-medium">✓ Found:</span>{' '}
                  <span className="text-foreground">{preview.formatted_address}</span>
                  <span className="block text-xs text-muted-foreground mt-1">
                    Not your address? Edit it above and check again.
                  </span>
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="radius">Travel radius: <span className="text-primary font-semibold">{radius} miles</span></Label>
              <input id="radius" type="range" min="1" max="50" value={radius}
                onChange={(e) => setRadius(e.target.value)} className="w-full accent-primary" />
            </div>
            <div className="flex items-start justify-between gap-4 rounded-lg border border-primary/20 bg-primary/5 p-4 opacity-70">
              <div>
                <p className="text-sm font-medium flex items-center gap-2">
                  <Lock className="h-3.5 w-3.5 text-primary" /> Smart Roaming
                  <span className="text-[10px] uppercase tracking-wider text-primary border border-primary/40 rounded-full px-2 py-0.5">Premium</span>
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Travelling? Showcasing? Dressing the elite? Your radius travels <em>with</em> you —
                  clients only ever see an indicative area, never your exact location.
                </p>
              </div>
              <div className="h-6 w-11 rounded-full bg-muted border border-border relative shrink-0 mt-1">
                <div className="h-5 w-5 rounded-full bg-muted-foreground/40 absolute left-0.5 top-0.5" />
              </div>
            </div>
          </div>
        );
      case STEP_DOCS:
        return (
          <div className="space-y-5">
            <h2 className="font-cormorant text-3xl font-bold">Your credentials</h2>
            <p className="text-sm text-muted-foreground">
              Upload at least <strong>{MIN_DOCUMENTS} documents</strong> — a minimum of one valid or earned
              competency (certificate or diploma), plus your <strong>ABN / proof of ATO registration</strong> and
              insurance. Profiles are reviewed and may take 2–5 minutes. Stored privately — only our review
              team sees them.
            </p>
            <div className="flex flex-col gap-1.5 p-3 rounded-lg border border-border/50">
              <OndaProgressO met={docsCount >= MIN_DOCUMENTS} label="Documents" count={docsCount} required={MIN_DOCUMENTS} />
              <OndaProgressO met={hasAbn} label="ABN / proof of ATO (Business License credential)" />
            </div>
            <CredentialsSection onCountChange={(n) => { setDocsCount(n); refreshProgress(); }} />
            <p className="text-xs text-muted-foreground">
              Next you'll verify your identity with Stripe (ID + a quick live selfie). Meeting these standards
              is an Onda prerequisite for payouts — it's how clients know every stylist is the real deal.
            </p>
          </div>
        );
      case STEP_PHOTOS:
        return (
          <div className="space-y-5">
            <h2 className="font-cormorant text-3xl font-bold">Show your work</h2>
            <p className="text-sm text-muted-foreground">
              Add at least <strong>{MIN_PHOTOS} photos</strong> — your work, before &amp; afters, a professional
              head-and-shoulders shot. They're reviewed before appearing on your profile.
            </p>
            <div className="p-3 rounded-lg border border-border/50">
              <OndaProgressO met={photos.length >= MIN_PHOTOS} label="Photos" count={photos.length} required={MIN_PHOTOS} />
            </div>
            {photos.length > 0 && (
              <div className="grid grid-cols-3 gap-3">
                {photos.map((p) => (
                  <div key={p.photo_id} className="relative group rounded-lg overflow-hidden border border-border/40 aspect-square">
                    <img src={p.photo_url} alt={p.caption || 'Portfolio'} className="w-full h-full object-cover"
                      loading="lazy" onError={(e) => { e.target.style.visibility = 'hidden'; }} />
                    <button type="button" onClick={() => deletePhoto(p.photo_id)} aria-label="Delete photo"
                      className="absolute top-1.5 right-1.5 p-1.5 rounded-md bg-black/60 text-red-400 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <label className={`flex flex-col items-center justify-center gap-2 p-8 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
              uploadingPhotos ? 'border-border/40 opacity-60' : 'border-primary/40 hover:border-primary'
            }`}>
              {uploadingPhotos
                ? <GoldenScissors size="md" />
                : <Camera className="h-8 w-8 text-primary" />}
              <span className="text-sm font-medium">{uploadingPhotos ? 'Uploading…' : 'Tap to add photos'}</span>
              <span className="text-xs text-muted-foreground">JPG, PNG or WEBP, up to 10 MB each — select several at once</span>
              <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden"
                disabled={uploadingPhotos}
                onChange={(e) => { uploadPhotos(Array.from(e.target.files || [])); e.target.value = ''; }} />
            </label>
          </div>
        );
      case STEP_VERIFY:
        return (
          <div className="space-y-5 text-center">
            <ShieldCheck className="h-12 w-12 text-primary mx-auto" />
            <h2 className="font-cormorant text-3xl font-bold">Verify it's really you</h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              To keep clients safe, we verify every stylist. You'll photograph your ID and take a quick live
              selfie — a few seconds, powered by Stripe. It's how we keep bots and fakes out.
            </p>
            {identityStatus === 'verified' ? (
              <>
                <p className="text-green-500 font-semibold">✓ Identity verified</p>
                <Button className="btn-primary px-10 py-6 text-lg" onClick={next}>
                  Continue <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </>
            ) : (identityStatus === 'processing' || identityStatus === 'pending') ? (
              <>
                <p className="text-muted-foreground">We're reviewing your verification…</p>
                <Button className="btn-primary px-10 py-6 text-lg" onClick={next}>
                  Continue <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <div>
                  <button type="button" onClick={startIdentity} className="text-xs text-muted-foreground hover:text-primary underline">
                    Redo verification
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="max-w-xs mx-auto flex flex-col gap-1.5 text-left">
                  <OndaProgressO met={docsCount >= MIN_DOCUMENTS} label="Credentials" count={docsCount} required={MIN_DOCUMENTS} />
                  <OndaProgressO met={photos.length >= MIN_PHOTOS} label="Photos" count={photos.length} required={MIN_PHOTOS} />
                  <OndaProgressO met={hasAbn} label="ABN / proof of ATO" />
                  <OndaProgressO met={socialCount >= 1} label="Social link" count={socialCount} required={1} />
                </div>
                <Button className="btn-primary px-10 py-6 text-lg" onClick={startIdentity}
                  disabled={saving || !requirementsMet}>
                  {saving ? <GoldenScissors size="sm" className="mr-2" /> : null}
                  Verify my identity <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                {!requirementsMet && (
                  <p className="text-xs text-muted-foreground">
                    Complete the checklist above to unlock verification.
                  </p>
                )}
              </>
            )}
          </div>
        );
      case STEP_CONFIRM:
        return (
          <div className="space-y-6 text-center">
            <span className="font-cormorant text-7xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent leading-none">
              O
            </span>
            <h2 className="font-cormorant text-3xl font-bold">You've met the Onda standard</h2>
            <div className="max-w-xs mx-auto flex flex-col gap-2 text-left">
              <OndaProgressO met={docsCount >= MIN_DOCUMENTS} label={`Documents uploaded (${docsCount} of ${MIN_DOCUMENTS})`} />
              <OndaProgressO met={photos.length >= MIN_PHOTOS} label={`Photos added (${photos.length} of ${MIN_PHOTOS})`} />
              <OndaProgressO met={hasAbn} label="ABN / business registration provided" />
            </div>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Save your profile and we'll take you straight to payout setup — the last step before clients
              can book you.
            </p>
            <Button className="btn-primary px-10 py-6 text-lg" onClick={saveProfileAndContinue} disabled={saving}>
              {saving ? <GoldenScissors size="sm" className="mr-2" /> : null}
              Save Profile <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        );
      case STEP_PAYOUT:
        return (
          <div className="space-y-5 text-center">
            <Landmark className="h-12 w-12 text-primary mx-auto" />
            <h2 className="font-cormorant text-3xl font-bold">Get paid</h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              Onda pays you securely through Stripe. On the next screen you'll add your bank and verify your
              identity — then your earnings (you keep <strong>85%</strong> of the service + <strong>100%</strong> of
              travel) land automatically after each booking.
            </p>
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <ShieldCheck className="h-4 w-4 text-green-500" /> Bank-grade security, powered by Stripe
            </div>
            <Button
              className={`btn-primary px-10 py-6 text-lg ${!isVerified ? 'opacity-40' : ''}`}
              onClick={startPayouts}
              disabled={saving || !isVerified}
            >
              {saving ? <GoldenScissors size="sm" className="mr-2" /> : (!isVerified ? <Lock className="mr-2 h-5 w-5" /> : null)}
              Set up payouts <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            {!isVerified && (
              <p className="text-xs text-muted-foreground">
                Payouts unlock once your identity verification is complete — you can finish the rest now
                and come back.
              </p>
            )}
            <div>
              <button type="button" onClick={next} className="text-xs text-muted-foreground hover:text-primary underline">
                I'll do this later
              </button>
            </div>
          </div>
        );
      case STEP_DONE:
        return (
          <div className="text-center space-y-5">
            <span className="font-cormorant text-7xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent leading-none block">
              O
            </span>
            <h2 className="font-cormorant text-4xl font-bold">
              Congratulations{firstName ? `, ${firstName}` : ''}!
            </h2>
            <p className="text-primary font-medium">You're verified, payable, and ready to shine.</p>
            <p className="text-muted-foreground max-w-md mx-auto">
              Your profile is with our review team — once approved, clients can find and book you.
              Welcome to Onda: your day, organised. Your brand, promoted. Anywhere, anytime.
            </p>
            <Button className="btn-primary px-10 py-6 text-lg" onClick={() => navigate('/stylist/dashboard')}>
              Go to my dashboard <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  const primaryAction = () => {
    if (step === STEP_PROFILE) return { label: 'Continue', fn: saveProfile };
    if (step === STEP_PRICING) return { label: 'Continue', fn: savePricing };
    if (step === STEP_AREA) return {
      label: previewMatches ? 'Continue' : 'Check your address first',
      fn: saveArea,
      disabled: !previewMatches,
    };
    if (step === STEP_DOCS) return {
      label: docsCount >= MIN_DOCUMENTS && hasAbn ? 'Continue' : `${docsCount} of ${MIN_DOCUMENTS} documents`,
      fn: next,
      disabled: docsCount < MIN_DOCUMENTS || !hasAbn,
    };
    if (step === STEP_PHOTOS) return {
      label: photos.length >= MIN_PHOTOS ? 'Continue' : `${photos.length} of ${MIN_PHOTOS} photos`,
      fn: next,
      disabled: photos.length < MIN_PHOTOS,
    };
    return null;
  };
  const action = primaryAction();

  return (
    <div className="min-h-screen py-12 px-6">
      <div className="container mx-auto max-w-2xl">
        <div className="mb-8">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
            <span>Step {Math.min(step + 1, STEPS.length)} of {STEPS.length}</span>
            <span>{STEPS[step]}</span>
          </div>
          <Progress value={((step + 1) / STEPS.length) * 100} />
        </div>

        <Card className="luxury-card">
          <CardContent className="p-8 min-h-[360px] flex flex-col justify-center">{stepBody()}</CardContent>
        </Card>

        {(step > STEP_WELCOME && step < STEP_DONE) && (
          <div className="flex items-center justify-between mt-6">
            <Button variant="ghost" onClick={back} disabled={saving}>
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
            {action && (
              <Button className={`btn-primary ${action.disabled ? 'opacity-40' : ''}`}
                onClick={action.fn} disabled={saving || action.disabled}>
                {saving ? <GoldenScissors size="sm" className="mr-2" /> : (action.disabled ? <Lock className="mr-2 h-4 w-4" /> : null)}
                {action.label} {!action.disabled && <ArrowRight className="ml-2 h-4 w-4" />}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
